import { useCallback, useEffect, useReducer } from 'react';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import {
  ANALYZING_FLOOR_MS,
  ANALYZING_CEILING_MS,
  BULLET_REVEAL_INTERVAL_MS,
} from './constants';

export type Phase =
  | 'analyzing'
  | 'revealing-bullets'
  | 'expanded-complete'
  | 'collapsed';

export type ViewState = 'analyzing' | 'expanded' | 'collapsed';

const phaseFromInitialState = (initialState: ViewState): Phase => {
  if (initialState === 'expanded') {
    return 'expanded-complete';
  }
  if (initialState === 'collapsed') {
    return 'collapsed';
  }
  return 'analyzing';
};

interface State {
  phase: Phase;
  floorReached: boolean;
  visibleBulletCount: number;
}

type Action =
  | { type: 'floorReached' }
  | { type: 'advance'; to: Phase }
  | { type: 'setBullets'; count: number };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'floorReached':
      return { ...state, floorReached: true };
    case 'advance':
      return { ...state, phase: action.to };
    case 'setBullets':
      return { ...state, visibleBulletCount: action.count };
    default:
      return state;
  }
}

export function useTypingAnimation(params: {
  bulletCount: number;
  isDataReady: boolean;
  initialState: ViewState;
}) {
  const { bulletCount, isDataReady, initialState } = params;
  const [state, dispatch] = useReducer(reducer, {
    phase: phaseFromInitialState(initialState),
    floorReached: false,
    visibleBulletCount: 0,
  });

  useEffect(() => {
    if (state.phase !== 'analyzing') {
      return undefined;
    }
    const floor = setTimeout(
      () => dispatch({ type: 'floorReached' }),
      ANALYZING_FLOOR_MS,
    );
    const ceil = setTimeout(() => {
      dispatch({ type: 'advance', to: 'revealing-bullets' });
    }, ANALYZING_CEILING_MS);
    return () => {
      clearTimeout(floor);
      clearTimeout(ceil);
    };
  }, [state.phase]);

  useEffect(() => {
    if (state.phase === 'analyzing' && state.floorReached && isDataReady) {
      dispatch({ type: 'advance', to: 'revealing-bullets' });
    }
  }, [state.phase, state.floorReached, isDataReady]);

  useEffect(() => {
    if (state.phase !== 'revealing-bullets') {
      return undefined;
    }
    dispatch({ type: 'setBullets', count: 0 });
    let count = 0;
    const interval = setInterval(() => {
      if (count < bulletCount) {
        count += 1;
        dispatch({ type: 'setBullets', count });
      } else {
        clearInterval(interval);
        dispatch({ type: 'advance', to: 'expanded-complete' });
      }
    }, BULLET_REVEAL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [state.phase, bulletCount]);

  const expand = useCallback(() => {
    dispatch({ type: 'advance', to: 'expanded-complete' });
    EclipsePackagePickerModule.SDK.aiRecommendationStore.expand();
  }, []);
  const collapse = useCallback(() => {
    dispatch({ type: 'advance', to: 'collapsed' });
    EclipsePackagePickerModule.SDK.aiRecommendationStore.collapse();
  }, []);

  const isTerminal = state.phase === 'expanded-complete';

  return {
    phase: state.phase,
    visibleBulletCount: isTerminal ? bulletCount : state.visibleBulletCount,
    expand,
    collapse,
  };
}
